import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService, MagicTranslateService } from '../app-services';
import { TranslateService } from '@ngx-translate/core';
import { LoginFormComponent } from '../login-form/login-form.component';

type LoginScope = 'admin' | 'super-admin' | 'model-admin';

@Component({
  selector: 'login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [LoginFormComponent],
})
export class LoginComponent {
  protected route = inject(ActivatedRoute);
  protected router = inject(Router);
  protected authService = inject(AuthService);
  protected snackBar = inject(MatSnackBar);
  protected translateService = inject(TranslateService);
  private magicTranslateService = inject(MagicTranslateService);

  readonly scopeLabelByScope: Record<LoginScope, string> = {
    admin: 'login-scope-admin',
    'super-admin': 'login-scope-super-admin',
    'model-admin': 'login-scope-model-admin',
  };

  get currentScope(): LoginScope {
    const routeScope = this.route.snapshot.paramMap.get('scope');
    const queryScope = this.route.snapshot.queryParamMap.get('scope');
    const value = (routeScope ?? queryScope ?? 'admin').toLowerCase();

    if (value === 'super-admin' || value === 'model-admin' || value === 'admin') {
      return value;
    }

    return 'admin';
  }

  get currentScopeLabel(): string {
    return this.scopeLabelByScope[this.currentScope];
  }

  login(userInfo: { email: string; password: string }) {
    let method: ReturnType<AuthService['loginAdmin']>;

    switch (this.currentScope) {
      case 'super-admin':
        method = this.authService.loginSuperAdmin(userInfo);
        break;
      case 'model-admin':
        method = this.authService.loginModelAdmin(userInfo);
        break;
      case 'admin':
      default:
        method = this.authService.loginAdmin(userInfo);
        break;
    }

    this.loginObs(method);
  }

  loginObs(method: ReturnType<AuthService['loginAdmin']>) {
    method.subscribe({
      next: res => {
        const paths: Record<string, string> = {
          ADMIN: '/users-management',
          SUPER_ADMIN: '/admin-management',
          MODEL_ADMIN: '/printable-data-management',
        };

        const language = res.data.payload.language;
        if (language) {
          this.magicTranslateService.updateUserLanguage(language);
        }
        this.router.navigate([paths[res.data.payload.role]]);
        this.translateService.get('login-success').subscribe(message => {
          this.snackBar.open(message, this.translateService.instant('ok'), { duration: 7000 });
        });
      },
      error: err => {
        switch (true) {
          case err.statusCode >= 500 && err.statusCode <= 511:
            this.translateService.get('server-error').subscribe(message => {
              this.snackBar.open(message, this.translateService.instant('ok'), { duration: 7000 });
            });
            break;
          case err.statusCode === 404:
            this.translateService.get('resource-not-found').subscribe(message => {
              this.snackBar.open(message, this.translateService.instant('ok'), { duration: 7000 });
            });
            this.router.navigate([{ outlets: { dialog: null, primary: ['error-404'] } }]);
            break;
          case err.statusCode === 401:
            this.translateService.get('incorrect-email-password').subscribe(message => {
              this.snackBar.open(message, this.translateService.instant('ok'), { duration: 7000 });
            });
            break;
          case err.statusCode === 403:
            this.translateService.get('no-acces').subscribe(message => {
              this.snackBar.open(message, this.translateService.instant('ok'), { duration: 7000 });
            });
            break;
          default:
            this.translateService.get('client-error').subscribe(message => {
              this.snackBar.open(message + ' ' + err.statusCode, this.translateService.instant('ok'), { duration: 7000 });
            });
            break;
        }
      },
    });
  }

  navigate(scope: LoginScope) {
    this.router.navigate(['/request-password-reset', scope]);
  }

  changeScope(scope: LoginScope) {
    this.router.navigate(['/login', scope]);
  }
}
